import {
    ProductUseCaseDocument,
    ProductUseCaseImage,
    setAppVersion,
    setAuthTokenAndOperatingMarket,
    setBaseURL,
    setLocale,
    uploadDocument,
    uploadImage,
} from '@schuettflix/client-file';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMarketContext } from '@/shared/context/MarketContext.tsx';
import { environment } from '@/environment.ts';
import { useAuthTokenContext } from '@/shared/context/AuthTokenContext.tsx';

export type FileUploadResponse =
    | {
          type: 'image';
          small_url: string;
          large_url: string;
          original_url: string;
          remotePath: string;
          id: string;
      }
    | {
          type: 'document';
          original_url: string;
          remotePath: string;
          id: string;
      };

export const useFileUpload = () => {
    const { i18n } = useTranslation();
    const { getToken } = useAuthTokenContext();
    const { marketCode } = useMarketContext();

    useEffect(() => {
        setLocale(i18n.language);
    }, [i18n.language]);

    setAuthTokenAndOperatingMarket({
        oktaToken: getToken(),
        market: marketCode,
    });
    setBaseURL(environment.MONOLITH_BASE_URL);
    setAppVersion(environment.FRONTEND_MONOLITH_APP_VERSION);

    const uploadImageMutation = useMutation({
        mutationKey: ['file', 'image', 'upload'],
        mutationFn: uploadImage,
    }).mutateAsync;

    const uploadDocumentMutation = useMutation({
        mutationKey: ['file', 'document', 'upload'],
        mutationFn: uploadDocument,
    }).mutateAsync;

    const uploadFile = useCallback(
        async (
            file: File,
            abortSignal: AbortSignal,
            onUploadProgressPercent: (progress: number) => void,
            productUseCases: {
                image: ProductUseCaseImage;
                document: ProductUseCaseDocument;
            }
        ): Promise<FileUploadResponse> => {
            if (file.type.startsWith('image/')) {
                const rawImageResponse = await uploadImageMutation({
                    file,
                    signal: abortSignal,
                    meta: { productUseCase: productUseCases.image },
                    onUploadProgressPercent,
                });
                return {
                    type: 'image',
                    small_url: rawImageResponse.data.url.small,
                    large_url: rawImageResponse.data.url.large,
                    original_url: rawImageResponse.data.url.original,
                    remotePath: `image/${rawImageResponse.data.urlPath}`,
                    id: rawImageResponse.data.uuid,
                };
            }

            const rawDocumentResponse = await uploadDocumentMutation({
                file,
                signal: abortSignal,
                meta: { productUseCase: productUseCases.document },
                onUploadProgressPercent,
            });
            return {
                type: 'document',
                original_url: rawDocumentResponse.data.url,
                remotePath: `document/${rawDocumentResponse.data.urlPath}`,
                id: rawDocumentResponse.data.uuid,
            };
        },
        [uploadImageMutation, uploadDocumentMutation]
    );

    return { uploadFile };
};
